/**
 * Game event system type definitions
 */

import { FishInstance, FishDefinition, TrapPullResult } from './fish';
import { SellTransaction } from './inventory';
import { FishingEvent } from './fishingEvent';
import { MilestoneDefinition } from './milestone';
import type { QuestDefinition } from './quest';
import type { WormDigResult } from './wormDig';

/**
 * Game event types (for EventBus)
 */
export enum GameEvent {
  // Fishing events
  FISHING_STARTED = 'fishing:started',
  FISHING_COMPLETED = 'fishing:completed',
  FISH_CAUGHT = 'fishing:caught',
  FISH_LOST = 'fishing:lost',

  // Inventory events
  INVENTORY_ADDED = 'inventory:added',
  INVENTORY_REMOVED = 'inventory:removed',
  INVENTORY_FULL = 'inventory:full',

  // Economy events
  FISH_SOLD = 'economy:sold',
  CURRENCY_GAINED = 'economy:gained',
  CURRENCY_SPENT = 'economy:spent',
  EQUIPMENT_PURCHASED = 'economy:purchased',
  EQUIPMENT_EQUIPPED = 'economy:equipped',

  // Shop events
  SHOP_UNLOCKED = 'shop:unlocked',
  SHOP_RESTOCKED = 'shop:restocked',

  // Quest events
  QUEST_ACCEPTED = 'quest:accepted',
  QUEST_COMPLETED = 'quest:completed',
  QUEST_PROGRESS = 'quest:progress',
  QUEST_ABANDONED = 'quest:abandoned',
  QUEST_FAILED = 'quest:failed',
  QUEST_EXPIRED = 'quest:expired',
  REWARDS_CLAIMED = 'quest:rewards_claimed',
  QUEST_BOARD_REFRESHED = 'quest:board_refreshed',
  CUSTOM_QUEST_UPDATED = 'custom_quest:updated',
  QUEST_CHAIN_UPDATED = 'quest_chain:updated',
  NEXT_QUEST_AVAILABLE = 'quest:next_available',
  NEW_QUEST_UNLOCKED = 'quest:new_unlocked',

  // Buddy events
  BUDDY_UNLOCKED = 'buddy:unlocked',
  BUDDY_ACTION = 'buddy:action',
  BUDDY_HIRED = 'buddy:hired',
  BUDDY_CAUGHT_FISH = 'buddy:caught_fish',

  // Hauler events
  HAULER_HIRED = 'hauler:hired',
  HAULER_STARTED_TRIP = 'hauler:started_trip',
  HAULER_SOLD_FISH = 'hauler:sold_fish',
  HAULER_RETURNED = 'hauler:returned',

  // Fishing event system
  FISHING_EVENT_TRIGGERED = 'fishing:event_triggered',

  // Level-up events
  PLAYER_LEVEL_UP = 'player:level_up',
  BUDDY_LEVEL_UP = 'buddy:level_up',

  // Sweet spot timing events
  SWEET_SPOT_HIT = 'fishing:sweet_spot_hit',

  // Fly cast minigame events
  FLY_CAST_COMPLETED = 'fishing:fly_cast_completed',

  // Trap events
  TRAP_SET = 'trap:set',
  TRAP_READY = 'trap:ready',
  TRAP_PULLED = 'trap:pulled',
  TRAP_BAIT_USED = 'trap:bait_used',

  // Milestone events
  MILESTONE_ACHIEVED = 'milestone:achieved',

  // Offline progress events
  OFFLINE_PROGRESS_CALCULATED = 'offline:progress_calculated',

  // Mentor bonus events (active play buffs buddies)
  MENTOR_BONUS_ACTIVATED = 'mentor:activated',
  MENTOR_BONUS_DEACTIVATED = 'mentor:deactivated',

  // Session goals events (daily objectives)
  SESSION_GOAL_COMPLETED = 'session:goal_completed',
  SESSION_GOALS_RESET = 'session:goals_reset',

  // Worm dig minigame events
  WORM_DIG_STARTED = 'wormdig:started',
  WORM_DIG_WORM_FOUND = 'wormdig:worm_found',
  WORM_DIG_COMPLETED = 'wormdig:completed',

  // Meta events
  GAME_LOADED = 'meta:loaded',
  GAME_SAVED = 'meta:saved',
  ACHIEVEMENT_UNLOCKED = 'meta:achievement',
}

/**
 * Event payload type mapping
 * Ensures type safety when emitting/listening to events
 */
export interface GameEventPayload {
  [GameEvent.FISHING_STARTED]: {
    attempt: { duration: number; zone: string };
  };

  [GameEvent.FISHING_COMPLETED]: {
    success: boolean;
  };

  [GameEvent.FISH_CAUGHT]: {
    fish: FishInstance;
    definition: FishDefinition;
  };

  [GameEvent.FISH_LOST]: Record<string, never>; // Empty object

  [GameEvent.INVENTORY_ADDED]: {
    fish: FishInstance;
  };

  [GameEvent.INVENTORY_REMOVED]: {
    definitionId: string;
    quality: string;
    count: number;
  };

  [GameEvent.INVENTORY_FULL]: Record<string, never>;

  [GameEvent.FISH_SOLD]: SellTransaction;

  [GameEvent.CURRENCY_GAINED]: {
    amount: number;
    source: string;
  };

  [GameEvent.CURRENCY_SPENT]: {
    amount: number;
    reason: string;
  };

  [GameEvent.EQUIPMENT_PURCHASED]: {
    outfitterId?: string;
    equipmentId: string;
    price: number;
    cost?: number; // Deprecated, use price
  };

  [GameEvent.EQUIPMENT_EQUIPPED]: {
    equipmentId: string;
    buddySlot?: number;
  };

  [GameEvent.SHOP_UNLOCKED]: {
    shopId: string;
    shopType: 'outfitter' | 'market';
  };

  [GameEvent.SHOP_RESTOCKED]: {
    shopId: string;
  };

  [GameEvent.QUEST_ACCEPTED]: {
    questId: string;
    quest: QuestDefinition;
  };

  [GameEvent.QUEST_COMPLETED]: {
    questId: string;
    completedAt: number;
  };

  [GameEvent.QUEST_PROGRESS]: {
    questId: string;
    objectiveId: string;
    currentValue: number;
    targetValue: number;
  };

  [GameEvent.QUEST_ABANDONED]: {
    questId: string;
  };

  [GameEvent.QUEST_FAILED]: {
    questId: string;
  };

  [GameEvent.QUEST_EXPIRED]: {
    questId: string;
  };

  [GameEvent.REWARDS_CLAIMED]: {
    questId: string;
    timestamp: number;
  };

  [GameEvent.QUEST_BOARD_REFRESHED]: {
    marketId: string;
    timestamp: number;
  };

  [GameEvent.CUSTOM_QUEST_UPDATED]: {
    questId: string;
  };

  [GameEvent.QUEST_CHAIN_UPDATED]: Record<string, never>;

  [GameEvent.NEXT_QUEST_AVAILABLE]: {
    completedQuestId: string;
    nextQuestDefinition: QuestDefinition;
  };

  [GameEvent.NEW_QUEST_UNLOCKED]: {
    questId: string;
    questName: string;
  };

  [GameEvent.BUDDY_UNLOCKED]: {
    buddyId: string;
  };

  [GameEvent.BUDDY_ACTION]: {
    buddyId: string;
    action: 'fishing' | 'selling';
    result?: Record<string, unknown>;
  };

  [GameEvent.BUDDY_HIRED]: {
    slotNumber: number;
    cost: number;
  };

  [GameEvent.BUDDY_CAUGHT_FISH]: {
    buddySlot: number;
    fish: FishInstance;
    timestamp: number;
  };

  [GameEvent.HAULER_HIRED]: {
    slotNumber: number;
    cost: number;
  };

  [GameEvent.HAULER_STARTED_TRIP]: {
    haulerSlot: number;
    cargoCount: number;
  };

  [GameEvent.HAULER_SOLD_FISH]: {
    haulerSlot: number;
    fishCount: number;
    value: number;
  };

  [GameEvent.HAULER_RETURNED]: {
    haulerSlot: number;
  };

  [GameEvent.GAME_LOADED]: {
    timestamp: number;
  };

  [GameEvent.GAME_SAVED]: {
    timestamp: number;
  };

  [GameEvent.FISHING_EVENT_TRIGGERED]: {
    event: FishingEvent;
  };

  [GameEvent.PLAYER_LEVEL_UP]: {
    newLevel: number;
    oldLevel: number;
  };

  [GameEvent.BUDDY_LEVEL_UP]: {
    buddyName: string;
    buddyType: string;
    newLevel: number;
    oldLevel: number;
  };

  [GameEvent.SWEET_SPOT_HIT]: {
    tier: 'perfect' | 'good' | 'miss';
  };

  [GameEvent.FLY_CAST_COMPLETED]: {
    tier: 'perfect' | 'good' | 'miss';
    accuracy: number;
    flyPatternId?: string;
  };

  // Trap events
  [GameEvent.TRAP_SET]: {
    trapId: string;        // Equipment ID of the trap
    baitId: string | null; // Equipment ID of bait, or null
    soakDurationMs: number;
  };

  [GameEvent.TRAP_READY]: {
    trapId: string;
    soakDurationMs: number;
  };

  [GameEvent.TRAP_PULLED]: TrapPullResult;

  [GameEvent.TRAP_BAIT_USED]: {
    baitId: string;
    remainingQuantity: number;
  };

  [GameEvent.MILESTONE_ACHIEVED]: {
    milestone: MilestoneDefinition;
  };

  [GameEvent.ACHIEVEMENT_UNLOCKED]: {
    achievementId: string;
  };

  // Offline progress
  [GameEvent.OFFLINE_PROGRESS_CALCULATED]: {
    offlineDurationMs: number;
    fishCaught: number;
    fishToInventory: number;
    fishSold: number;
    goldEarned: number;
    buddyCatches: number;
    haulerTrips: number;
  };

  // Mentor bonus
  [GameEvent.MENTOR_BONUS_ACTIVATED]: Record<string, never>;
  [GameEvent.MENTOR_BONUS_DEACTIVATED]: Record<string, never>;

  // Session goals
  [GameEvent.SESSION_GOAL_COMPLETED]: {
    goalId: string;
    rewardGold: number;
    rewardXP: number;
  };
  [GameEvent.SESSION_GOALS_RESET]: {
    goals: Array<{ id: string; description: string; targetValue: number }>;
  };

  // Worm dig minigame
  [GameEvent.WORM_DIG_STARTED]: {
    wormCount: number;
  };
  [GameEvent.WORM_DIG_WORM_FOUND]: {
    wormsFound: number;
    totalWorms: number;
  };
  [GameEvent.WORM_DIG_COMPLETED]: WormDigResult;
}

/**
 * Event handler function type
 */
export type EventHandler<T = any> = (payload: T) => void;
